
import { useState, useEffect } from 'react';
import { FormFieldType } from '@/types/form';
import { useForm } from '@/context/FormContext';
import { Worksheet, useWorksheetSearch } from '@/hooks/useWorksheetSearch';
import { toast } from '@/hooks/use-toast';
import { WorksheetOption } from './types';
import { getWorksheetIdFromSelection, hasAvailableSheets, getSubjectDataFromWorksheet } from './utils';

interface UseWorksheetSelectionOptions {
  element: FormFieldType;
  otherSubjectField: string;
  subjectDataField: string;
  worksheetIdField?: string;
}

export const useWorksheetSelection = ({
  element,
  otherSubjectField,
  subjectDataField,
  worksheetIdField
}: UseWorksheetSelectionOptions) => {
  const { formData, setField } = useForm();
  const [selectedWorksheet, setSelectedWorksheet] = useState<Worksheet | null>(null);
  const [otherSubject, setOtherSubject] = useState<boolean>(
    formData[otherSubjectField] === true
  );
  
  const { 
    searchQuery, 
    setSearchQuery, 
    worksheets, 
    isLoading 
  } = useWorksheetSearch('');

  // Load existing selection on component mount
  useEffect(() => {
    // Check if we have stored worksheet data in the form
    if (formData[subjectDataField]) {
      try {
        const worksheetData = typeof formData[subjectDataField] === 'string' 
          ? JSON.parse(formData[subjectDataField] as string) 
          : formData[subjectDataField];
          
        if (worksheetData) {
          setSelectedWorksheet(worksheetData as Worksheet);
        }
      } catch (error) {
        console.error("Error parsing stored worksheet data:", error);
      }
    }

    // Set other_subject checkbox state based on form data
    if (formData[otherSubjectField] === true) {
      setOtherSubject(true);
    }
  }, [formData[subjectDataField], formData[otherSubjectField], otherSubjectField, subjectDataField]);

  // Handle worksheet selection
  const handleSelectWorksheet = (option: WorksheetOption | null) => {
    if (!option) {
      handleClearSelection();
      return;
    }
    
    const worksheet = option.worksheet;
    setSelectedWorksheet(worksheet);
    
    // Store the full worksheet data as JSON
    const worksheetData = getSubjectDataFromWorksheet(worksheet);
    setField(subjectDataField, worksheetData);
    
    // If worksheetIdField is specified, store the worksheet ID as a string
    if (worksheetIdField && worksheet.id) {
      setField(worksheetIdField, String(worksheet.id)); 
      console.log(`Setting worksheet ID field ${worksheetIdField} to:`, worksheet.id);
    }
    
    // If targetField is specified, update it with the worksheet name
    if (element.targetField) {
      setField(element.targetField, worksheet.name);
    }
    
    // Reset other_subject when a worksheet is selected
    if (otherSubject) {
      setOtherSubject(false);
      setField(otherSubjectField, false);
    }
    
    // Check if the worksheet has available sheets and notify the user
    const hasSheets = hasAvailableSheets(worksheet);
    const toastMessage = hasSheets 
      ? `${worksheet.name} - ${worksheet.subject} (มีชีทให้ดาวน์โหลด)`
      : `${worksheet.name} - ${worksheet.subject}`;
    
    toast({
      title: "เลือกเวิร์กชีท",
      description: toastMessage,
    });
  };

  // Handle clearing selection
  const handleClearSelection = () => {
    setSelectedWorksheet(null);
    setSearchQuery("");
    
    // Clear the subject_data field
    setField(subjectDataField, null);
    
    // Clear the worksheet ID field if specified
    if (worksheetIdField) {
      setField(worksheetIdField, null);
    }
    
    // Clear the target field as well if specified
    if (element.targetField) {
      setField(element.targetField, "");
    }
  };

  // Handle other subject checkbox
  const handleOtherSubjectChange = (checked: boolean) => {
    setOtherSubject(checked);
    setField(otherSubjectField, checked);
    
    // Clear worksheet selection when "other" is checked
    if (checked && selectedWorksheet) {
      setSelectedWorksheet(null);
      setSearchQuery("");
      setField(subjectDataField, null);
      
      // Clear the worksheet ID field if specified
      if (worksheetIdField) {
        setField(worksheetIdField, null);
      }
      
      // Clear the target field if specified
      if (element.targetField) {
        setField(element.targetField, "อื่นๆ");
      }
    } else if (!checked && element.targetField) {
      setField(element.targetField, "");
    }
    
    // Toggle the readOnly property of the target field
    if (element.targetField) {
      // Find the target field in the DOM and update its disabled status
      setTimeout(() => {
        const targetInputField = document.getElementById(element.targetField as string) as HTMLInputElement;
        if (targetInputField) {
          targetInputField.disabled = !checked;
          targetInputField.readOnly = !checked;
        }
      }, 100);
    }
  };

  // Effect to set target field to readonly when component mounts or otherSubject changes
  useEffect(() => {
    if (element.targetField) {
      // Find the target field in the DOM and update its disabled status
      setTimeout(() => {
        const targetField = document.getElementById(element.targetField as string) as HTMLInputElement;
        if (targetField) {
          targetField.disabled = !otherSubject;
          targetField.readOnly = !otherSubject;
        }
      }, 100);
    }
  }, [otherSubject, element.targetField]);

  // Convert worksheets array to options format for react-select
  const options: WorksheetOption[] = worksheets.map(worksheet => ({
    value: worksheet.id,
    label: `${worksheet.name} - ${worksheet.subject} (${worksheet.grade_level})${hasAvailableSheets(worksheet) ? " 📄" : ""}`,
    worksheet: worksheet
  }));

  // Prepare the currently selected option for react-select
  const selectedOption = selectedWorksheet ? {
    value: selectedWorksheet.id,
    label: `${selectedWorksheet.name} - ${selectedWorksheet.subject} (${selectedWorksheet.grade_level})${hasAvailableSheets(selectedWorksheet) ? " 📄" : ""}`,
    worksheet: selectedWorksheet
  } : null;

  return {
    selectedWorksheet,
    otherSubject,
    searchQuery,
    setSearchQuery,
    worksheets,
    isLoading,
    handleSelectWorksheet,
    handleOtherSubjectChange,
    options,
    selectedOption
  };
};
